// features/hives/lib/useHivesFilters.ts
import {useEffect, useRef, useState} from "react";
import {useForm, useWatch} from "react-hook-form";
import {IDropdownMenuItem} from "@atomaro/ui-kit/components/DropdownMenu/types";
import {useApiariesStore} from "entities/apiaries";
import {useHivesStore} from "entities/hives";
import {useDictionariesStore} from "entities/dictionaries";
import {useDebounceCallback} from "shared/lib/debounce/useDebounce";
import {toast} from "react-toastify";

export type HivesFiltersFormValues = {
    numberQuery: string;
    hiveIds: string[];
    year: string | null;
    markNumber: string | null;
    breed: string | null;
    framesFrom: string;
    framesTo: string;
    powerFrom: string;
    powerTo: string;
    broodFrom: string;
    broodTo: string;
    dictionariesIds: string[];
    sortBy: string;
    sort: "ASC" | "DESC";
};

const defaults: HivesFiltersFormValues = {
    numberQuery: "",
    hiveIds: [],
    year: null,
    markNumber: null,
    breed: null,
    framesFrom: "",
    framesTo: "",
    powerFrom: "",
    powerTo: "",
    broodFrom: "",
    broodTo: "",
    dictionariesIds: [],
    sortBy: "updatedAt",
    sort: "DESC",
};

export type ShortLabels = {
    frames: string;
    power: string;
    brood: string;
    dictionariesIds: string;
    year: string;
    markNumber: string;
    breed: string;
};

const shortDefault: ShortLabels = {
    frames: "",
    power: "",
    brood: "",
    dictionariesIds: "",
    year: "",
    markNumber: "",
    breed: "",
};

export const useHivesFilters = () => {
    const apiaryId = useApiariesStore((s) => s.currentApiaryId);
    const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
    const dictionaries = useDictionariesStore((s) => s.dictionaries);

    const { control, getValues, setValue, resetField, reset } = useForm<HivesFiltersFormValues>({ defaultValues: defaults });
    const formValues = useWatch({ control });

    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [shortFilterOpenSelector, setShortFilterOpenSelector] = useState<string | null>(null);
    const [shortLabels, setShortLabels] = useState<ShortLabels>(shortDefault);
    const [settedFiltersCounter, setSettedFiltersCounter] = useState(0);
    const [dictionaryItems, setDictionaryItems] = useState(dictionaries.map((d) => ({ id: d.id, name: d.name, isSelected: false })));

    const lastParams = useRef<any>(null);

    const buildParams = () => {
        const formData = getValues();
        
        const normalizedParams = {
            apiaryId,
            numberQuery: formData.numberQuery?.trim() || null,
            hiveIds: formData.hiveIds || [],
            year: formData.year,
            markNumber: formData.markNumber,
            breed: formData.breed,
            framesFrom: formData.framesFrom || null,
            framesTo: formData.framesTo || null,
            powerFrom: formData.powerFrom || null,
            powerTo: formData.powerTo || null,
            broodFrom: formData.broodFrom || null,
            broodTo: formData.broodTo || null,
            dictionariesIds: formData.dictionariesIds || [],
            sortBy: formData.sortBy || "updatedAt",
            sort: formData.sort || "DESC",
            page: { index: 0, size: 10 },
        };
        
        return normalizedParams;
    };

    const shallowEqual = (a: any, b: any): boolean => {
        return JSON.stringify(a) === JSON.stringify(b);
    };

    const makeRange = (from?: string, to?: string): string => {
        if (from && to) return `от ${from} до ${to}`;
        if (from) return `от ${from}`;
        if (to) return `до ${to}`;
        return "";
    };

    const refreshLabelsAndCounter = (): number => {
        const currentValues = getValues();
        const selectedDictionaryIds = (currentValues.dictionariesIds || []) as string[];

        const updatedLabels: ShortLabels = {
            frames: makeRange(currentValues.framesFrom, currentValues.framesTo),
            power: makeRange(currentValues.powerFrom, currentValues.powerTo),
            brood: makeRange(currentValues.broodFrom, currentValues.broodTo),
            dictionariesIds: (() => {
                const dictCount = selectedDictionaryIds?.length ?? 0;
                if (dictCount === 0) return "";
                const firstDict = dictionaries.find((d) => d.id === selectedDictionaryIds[0])?.name || "";
                const remainingCount = dictCount - 1;
                return remainingCount > 0 ? `${firstDict} +${remainingCount}` : firstDict;
            })(),
            year: currentValues.year || "",
            markNumber: currentValues.markNumber || "",
            breed: currentValues.breed || "",
        };

        setShortLabels(updatedLabels);

        let activeFiltersCount = 0;
        if (currentValues.year) activeFiltersCount++;
        if (currentValues.markNumber) activeFiltersCount++;
        if (currentValues.breed) activeFiltersCount++;
        if (currentValues.framesFrom || currentValues.framesTo) activeFiltersCount++;
        if (currentValues.powerFrom || currentValues.powerTo) activeFiltersCount++;
        if (currentValues.broodFrom || currentValues.broodTo) activeFiltersCount++;
        if (selectedDictionaryIds.length > 0) activeFiltersCount++;
        
        setSettedFiltersCounter(activeFiltersCount);
        return activeFiltersCount;
    };

    const submit = async (silent?: boolean): Promise<void> => {
        if (!apiaryId) return;
        
        const requestParams = buildParams();
        
        if (shallowEqual(requestParams, lastParams.current)) return;

        lastParams.current = requestParams;
        
        await getHivesFromServer(requestParams as any, true).catch(console.warn);

        const filtersCount = refreshLabelsAndCounter();
        
        if (!filtersCount && !silent) {
            toast.warn("Необходимо выбрать хотя бы один фильтр");
        }

        setIsFiltersOpen(false);
    };

    const submitRef = useRef(submit);
    submitRef.current = submit;
    
    const submitDebounced = useDebounceCallback(() => submitRef.current(true), 600);

    const onSearchChange = (val: string) => {
        setValue("numberQuery", val);
        submitDebounced();
    };

    const onHivesChange = (items: IDropdownMenuItem[]) => {
        setValue("hiveIds", items.map((i) => `${i.key}`));
        submitDebounced();
    };

    const onRangeChange = (name: keyof HivesFiltersFormValues, value: string) => {
        setValue(name, value.replace(/[^\d]/g, ""));
        refreshLabelsAndCounter();
    };

    const onSortChange = (value: string | number) => {
        const stringValue = String(value);
        
        // Обрабатываем суффикс -ASC для переключения порядка сортировки
        if (stringValue.includes("-ASC")) {
            const sortBy = stringValue.replace("-ASC", "");
            const currentSortBy = getValues("sortBy");
            const currentSort = getValues("sort");
            
            if (sortBy === currentSortBy) {
                setValue("sort", currentSort === "ASC" ? "DESC" : "ASC");
            } else {
                setValue("sortBy", sortBy);
                setValue("sort", "ASC");
            }
        } else {
            const currentValue = getValues("sortBy");
            const currentSort = getValues("sort");
            
            // Если кликают на тот же элемент, меняем направление сортировки
            if (stringValue === currentValue) {
                setValue("sort", currentSort === "ASC" ? "DESC" : "ASC");
            } else {
                setValue("sortBy", stringValue);
                setValue("sort", "DESC"); // По умолчанию DESC
            }
        }
        
        submitDebounced();
    };

    const setHasDictionary = (id: string) => {
        const ids = (getValues("dictionariesIds") || []) as string[];
        const next = ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id];
        setValue("dictionariesIds", next);
        setDictionaryItems(dictionaries.map((d) => ({ id: d.id, name: d.name, isSelected: next.includes(d.id) })));
        refreshLabelsAndCounter();
        submit(true);
    };

    const onDictionaryReset = () => {
        setValue("dictionariesIds", []);
        setDictionaryItems(dictionaries.map((d) => ({ id: d.id, name: d.name, isSelected: false })));
        refreshLabelsAndCounter();
    };

    const openShortFilter = (key: string) => setShortFilterOpenSelector(key);
    const closeShortFilter = (submitNow?: boolean) => {
        setShortFilterOpenSelector(null);
        if (submitNow) submit(true);
    };

    const onResetField = (key: string) => {
        switch (key) {
            case "frames":
                resetField("framesFrom");
                resetField("framesTo");
                break;
            case "power":
                resetField("powerFrom");
                resetField("powerTo");
                break;
            case "brood":
                resetField("broodFrom");
                resetField("broodTo");
                break;
            case "dictionariesIds":
                onDictionaryReset();
                return;
            case "year":
                setValue("year", null);
                break;
            case "markNumber":
                setValue("markNumber", null);
                break;
            case "breed":
                setValue("breed", null);
                break;
            default:
                break;
        }
        refreshLabelsAndCounter();
    };

    const onResetAll = () => {
        reset(defaults);
        setDictionaryItems(dictionaries.map((d) => ({ id: d.id, name: d.name, isSelected: false })));
        setShortLabels(shortDefault);
        setSettedFiltersCounter(0);
        closeShortFilter();
        submit(true);
    };

    useEffect(() => {
        //setDictionaryItems(dictionaries.map((d) => ({ id: d.id, name: d.name, isSelected: false })));
        setDictionaryItems(dictionaries.filter((el) => el.type === "DIARY").map((d) => ({ id: d.id, name: d.name, isSelected: false })));
    }, [dictionaries]);

    const apiaryIdRef = useRef(apiaryId);
    
    useEffect(() => {
        if (apiaryIdRef.current !== apiaryId) {
            apiaryIdRef.current = apiaryId;
            submit(true);
        }
    }, [apiaryId]);

    return {
        control,
        formValues,
        dictionaryItems,
        settedFiltersCounter,
        isFiltersOpen,
        shortFilterOpenSelector,
        shortLabels,
        setIsFiltersOpen,
        openShortFilter,
        closeShortFilter,
        onSearchChange,
        onHivesChange,
        onRangeChange,
        onSortChange,
        setHasDictionary,
        onDictionaryReset,
        onResetField,
        onResetAll,
        submit,
        reset,
        getValues,
    };
};
